import {
  type Coordinate,
  type EventPayload,
  type RunRecord,
  type RunState,
  applyEvent,
  reduce,
  signById,
} from '@orgsim/shared';
import { useEffect, useRef, useState } from 'react';
import { DrawTools } from '../map/DrawTools';
import { DrawingLayer } from '../map/DrawingLayer';
import { MapView } from '../map/MapView';
import { PatientMarker } from '../map/PatientMarker';
import { SignMarker } from '../map/SignMarker';
import { SignPalette, placementOrganisation } from '../signs/SignPalette';
import { type ChannelMessage, openChannel, post } from '../store/channel';
import { getRun } from '../store/db';
import { examineeRecord } from '../store/examineeView';

export function MapWindow({ runId }: { runId: string }) {
  const [run, setRun] = useState<RunRecord | null>(null);
  const [state, setState] = useState<RunState | null>(null);
  const [mainGone, setMainGone] = useState(false);
  const [selectedSign, setSelectedSign] = useState<string | null>(null);
  const [drawing, setDrawing] = useState(false);
  const channel = useRef<BroadcastChannel | null>(null);
  // Set by the sync handler so the fallback timeout below can tell a healthy,
  // still-syncing main window apart from one that never answers.
  const synced = useRef(false);

  useEffect(() => {
    const ch = openChannel(runId);
    channel.current = ch;
    ch.onmessage = (m: MessageEvent<ChannelMessage>) => {
      const msg = m.data;
      if (msg.kind === 'sync') {
        synced.current = true;
        setRun(msg.run);
        setState(reduce(msg.run.scenario, msg.run.events));
        setMainGone(false);
      }
      if (msg.kind === 'event') setState((s) => (s ? applyEvent(s, msg.event) : s));
      if (msg.kind === 'closed') setMainGone(true);
    };
    post(ch, { kind: 'requestSync' });
    // Fallback if the main window does not answer in time: show the stored run
    // read-only. Only fires when no sync arrived, so a healthy main window that
    // merely answers a bit late never gets flagged as gone.
    const t = setTimeout(async () => {
      if (synced.current) return;
      const record = await getRun(runId);
      if (synced.current) return; // sync arrived while we were reading storage
      if (record) {
        // Storage holds the instructor's full record; strip it just like the sync message.
        const stored = examineeRecord(record);
        setRun((r) => r ?? stored);
        setState((s) => s ?? reduce(stored.scenario, stored.events));
      }
      setMainGone(true);
    }, 1500);
    return () => {
      clearTimeout(t);
      ch.close();
    };
  }, [runId]);

  const propose = (payload: EventPayload) => {
    if (channel.current && !mainGone) post(channel.current, { kind: 'propose', payload });
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedSign(null);
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && channel.current && !mainGone) {
        e.preventDefault();
        post(channel.current, { kind: 'undo' });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mainGone]);

  if (!run || !state) {
    return (
      <div>
        {mainGone ? (
          <div className="bg-red-900 px-3 py-1 text-center text-sm">
            Hauptfenster nicht erreichbar. Anzeige ohne Bedienung.
          </div>
        ) : null}
        <div className="p-6 text-sm text-neutral-400">Warte auf das Hauptfenster …</div>
      </div>
    );
  }

  const onMapClick = (position: Coordinate) => {
    // Leaflet fires every click listener registered on the map, so DrawTools cannot
    // suppress this one on its own; the drawing flag gates it here instead.
    if (drawing || !selectedSign) return;
    propose({
      type: 'SignPlaced',
      signId: crypto.randomUUID(),
      signType: selectedSign,
      position,
      label: signById(selectedSign).label,
      organisation: placementOrganisation(selectedSign),
    });
    setSelectedSign(null);
  };

  return (
    <div className="flex h-full flex-col">
      {mainGone ? (
        <div className="bg-red-900 px-3 py-1 text-center text-sm">
          Hauptfenster nicht erreichbar. Anzeige ohne Bedienung.
        </div>
      ) : null}
      <div className="border-b border-neutral-800 px-2 py-1">
        <SignPalette compact selected={selectedSign} onSelect={setSelectedSign} />
      </div>
      <div className="min-h-0 flex-1">
        <MapView center={run.scenario.situation.location} zoom={5} onClick={onMapClick}>
          {Object.values(state.signs).map((s) => (
            <SignMarker
              key={s.id}
              sign={s}
              draggable={!mainGone}
              onMove={(position) => propose({ type: 'SignMoved', signId: s.id, position })}
            />
          ))}
          {Object.values(state.patients).map((p) => (
            <PatientMarker key={p.id} patient={p} />
          ))}
          <DrawingLayer drawings={Object.values(state.drawings)} />
          {!mainGone ? (
            <DrawTools
              onDrawing={(d) => propose({ type: 'DrawingAdded', drawing: d })}
              drawingActive={setDrawing}
            />
          ) : null}
        </MapView>
      </div>
    </div>
  );
}
